import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CircleHelp } from "lucide-react";
import type { ParamSpec } from "@/api/types";
import { REDACTED_VALUE } from "@/api/constants";
import { isRedacted } from "@/lib/utils";
import { toEd25519Wire } from "@/lib/keys";
import {
  propertyLabel,
  propertyOptionLabel,
  propertyUnit,
  formatEngineQty,
  engineQtyAmountText,
  engineQtyMode,
  engineQtyUnit,
  isLeverageField,
  leverageTextToMargin,
  marginToLeverageText,
  ENGINE_FEE_UNITS,
  ENGINE_QTY_MODES,
  type EngineQtyMode,
} from "@/lib/engine-properties";
import { ScriptEditor } from "./ScriptEditor";
import { Ed25519KeyInput } from "./Ed25519KeyInput";

const HMAC_FIELDS = new Set(["api_key", "secret", "api_secret", "secret_key"]);

function hideHmacFields(specs: ParamSpec[]): boolean {
  return specs.some((s) => s.type === "ed25519");
}

function isHiddenCredential(spec: ParamSpec, hideHmac: boolean): boolean {
  return hideHmac && HMAC_FIELDS.has(spec.name);
}

/** Pine `input.time` / backend datetime params store Unix milliseconds. */
function msToDatetimeLocal(ms: unknown): string {
  const n = typeof ms === "number" ? ms : Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "";
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function datetimeLocalToMs(s: string): number {
  if (!s) return 0;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Accept both 0.1 and 0,1 (locale decimal comma). */
function parseDecimalInput(raw: string): number {
  const normalized = raw.trim().replace(",", ".");
  return parseFloat(normalized);
}

function isPartialDecimalInput(raw: string): boolean {
  return raw === "" || raw === "-" || raw === "." || raw === "," || /^-?\d*[.,]?\d*$/.test(raw);
}

function defaultsFromSpecs(specs: ParamSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of Array.isArray(specs) ? specs : []) {
    if (s.default !== undefined && s.default !== null) out[s.name] = s.default;
    else if (s.type === "bool") out[s.name] = false;
    else if (s.type === "datetime") out[s.name] = 0;
    else if (s.type === "int" || s.type === "float") out[s.name] = s.min ?? 0;
    else out[s.name] = "";
  }
  return out;
}

/** Merge spec defaults under current values so saves include unvisited tabs. */
export function fillParamDefaults(
  values: Record<string, unknown>,
  specs: ParamSpec[]
): Record<string, unknown> {
  const list = Array.isArray(specs) ? specs : [];
  return { ...defaultsFromSpecs(list), ...values };
}

function isWideField(spec: ParamSpec): boolean {
  return spec.type === "script" || spec.type === "ed25519";
}

/** Description that only repeats the field name adds clutter — skip it. */
function usefulDescription(spec: ParamSpec): string | null {
  const d = (spec.description || "").trim();
  if (!d) return null;
  if (d.toLowerCase() === spec.name.toLowerCase()) return null;
  if (d.toLowerCase() === propertyLabel(spec).toLowerCase()) return null;
  return d;
}

function columnsClass(columns: 1 | 2 | 3): string {
  if (columns === 3) return "grid gap-x-3 gap-y-2.5 sm:grid-cols-2 xl:grid-cols-3";
  if (columns === 2) return "grid gap-x-3 gap-y-2.5 sm:grid-cols-2";
  return "grid gap-3";
}

/**
 * A margin percentage edited as TradingView's leverage ("2x"). The field keeps
 * what is being typed and reports the margin it stands for; 0, an empty field
 * and "Infinity" are margin 0, shown as "Infinity".
 */
function LeverageInput({
  label,
  margin,
  disabled,
  onChange,
}: {
  label: string;
  margin: unknown;
  disabled?: boolean;
  onChange: (margin: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={draft ?? marginToLeverageText(margin)}
      disabled={disabled}
      className="w-[7.5rem]"
      onFocus={(e) => {
        // Edit the bare number: "2x" becomes "2", "Infinity" an empty field.
        const pct = Number(margin);
        setDraft(pct > 0 ? marginToLeverageText(margin).replace(/x$/, "") : "");
        requestAnimationFrame(() => e.target.select());
      }}
      onChange={(e) => {
        const text = e.target.value;
        setDraft(text);
        const next = leverageTextToMargin(text);
        // An empty field is only Infinity once it is left that way.
        if (next != null && text.trim() !== "") onChange(next);
      }}
      onBlur={() => {
        if (draft != null) {
          const next = leverageTextToMargin(draft);
          if (next != null) onChange(next);
        }
        setDraft(null);
      }}
    />
  );
}

function TipIcon({ tip }: { tip: string }) {
  return (
    <span tabIndex={0} className="group relative inline-flex shrink-0 cursor-help rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]">
      <CircleHelp
        className="h-3.5 w-3.5 text-[var(--text-muted)] opacity-70"
        aria-label={tip}
      />
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden w-52 -translate-x-1/2 rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2.5 py-2 text-left text-[11px] leading-relaxed text-[var(--text-secondary)] shadow-lg group-hover:block group-focus-within:block"
      >
        {tip}
      </span>
    </span>
  );
}

function FieldLabelName({ spec }: { spec: ParamSpec }) {
  return (
    <>
      <span className="truncate">{propertyLabel(spec)}</span>
      {spec.disabled && (
        <span className="shrink-0 text-xs text-[var(--text-muted)]">(Not supported yet)</span>
      )}
    </>
  );
}

export function DynamicForm({
  specs,
  values,
  onChange,
  loading = false,
  readOnly = false,
  maskSecrets = true,
  columns = 1,
  layout = "grid",
  density = "comfortable",
  keyGuide = true,
}: {
  specs: ParamSpec[];
  values: Record<string, unknown>;
  onChange?: (next: Record<string, unknown>) => void;
  loading?: boolean;
  readOnly?: boolean;
  maskSecrets?: boolean;
  /** Multi-column layout. With layout="rows", places fields in an N-column grid within each section. */
  columns?: 1 | 2 | 3;
  /**
   * `grid` — stacked label-above-control cards (default).
   * `rows` — TradingView-style label left / control right, section headers.
   */
  layout?: "grid" | "rows";
  /** `compact` — concept properties drawer: 13px rows, hint under the label, short controls. */
  density?: "comfortable" | "compact";
  /** Show the Ed25519 "How to connect" box; off when the page explains the steps itself. */
  keyGuide?: boolean;
}) {
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const rows = layout === "rows";
  const dense = !rows && columns > 1;
  const compact = density === "compact";
  const unitClass = compact
    ? "shrink-0 text-[11px] text-[var(--text-muted)]"
    : "min-w-[4.5rem] rounded-[var(--radius-input)] border border-[var(--border)] bg-[var(--input-bg)] px-2.5 py-2 text-center text-xs text-[var(--text-secondary)]";

  // Keep latest values in a ref so rapid edits (and defaults fill) don't spread a
  // stale `values` snapshot and clobber sibling fields when the parent merges.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const change = onChangeRef.current;
    if (!change || specs.length === 0) return;
    const defaults = defaultsFromSpecs(specs);
    const current = valuesRef.current;
    const missing = specs.some((s) => current[s.name] === undefined);
    if (!missing) return;
    const next = { ...current };
    for (const s of specs) {
      if (next[s.name] === undefined) next[s.name] = defaults[s.name];
    }
    valuesRef.current = next;
    change(next);
  }, [specs]);

  const hideHmac = hideHmacFields(specs);
  const groups = useMemo(() => {
    const map = new Map<string, ParamSpec[]>();
    for (const s of specs) {
      if (isHiddenCredential(s, hideHmac) || s.hidden) continue;
      const g = s.group || "General";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(s);
    }
    return Array.from(map.entries());
  }, [specs, hideHmac]);

  const needle = filter.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!needle) return groups;
    return groups
      .map(([group, fields]) => {
        const matched = fields.filter((s) => {
          const desc = usefulDescription(s) || "";
          const label = propertyLabel(s);
          return (
            s.name.toLowerCase().includes(needle) ||
            label.toLowerCase().includes(needle) ||
            desc.toLowerCase().includes(needle) ||
            group.toLowerCase().includes(needle)
          );
        });
        return [group, matched] as [string, ParamSpec[]];
      })
      .filter(([, fields]) => fields.length > 0);
  }, [groups, needle]);

  const showFilter = dense && specs.length > 10;
  const onlyGeneral =
    groups.length === 1 && (groups[0]?.[0] ?? "General") === "General";
  // Only show section headers when the script/engine actually defines groups.
  // Ungrouped Pine inputs stay a flat list (no fake "General").
  const showGroupHeaders = groups.length > 1 || !onlyGeneral;

  function setField(name: string, value: unknown) {
    if (!onChange || readOnly) return;
    const next = { ...valuesRef.current, [name]: value };
    valuesRef.current = next;
    onChange(next);
  }

  /** Several fields in one change, e.g. an amount and the unit it counts in. */
  function setFields(patch: Record<string, unknown>) {
    if (!onChange || readOnly) return;
    const next = { ...valuesRef.current, ...patch };
    valuesRef.current = next;
    onChange(next);
  }
  const has = (name: string) => specs.some((sp) => sp.name === name);

  function toggleGroup(group: string) {
    setCollapsed((c) => ({ ...c, [group]: !c[group] }));
  }

  function renderControl(spec: ParamSpec): ReactNode {
    const raw = values[spec.name];
    const fieldDisabled = readOnly || spec.disabled === true;
    const secret = spec.secret && maskSecrets;
    const shown =
      secret &&
      !reveal[spec.name] &&
      (isRedacted(raw) || (typeof raw === "string" && raw.length > 0))
        ? "••••••••"
        : raw ?? "";
    const unit = rows ? propertyUnit(spec) : undefined;

    if (spec.type === "bool") {
      return (
        <input
          type="checkbox"
          checked={raw === true || raw === "true"}
          disabled={fieldDisabled}
          onChange={(e) => setField(spec.name, e.target.checked)}
        />
      );
    }

    if (spec.type === "select") {
      return (
        <select
          value={String(raw ?? "")}
          disabled={fieldDisabled}
          onChange={(e) => setField(spec.name, e.target.value)}
          className={rows ? (compact ? "w-[7.5rem]" : "min-w-[14rem] max-w-full") : undefined}
        >
          <option value="">Select…</option>
          {(spec.options || []).map((o) => (
            <option key={o} value={o}>
              {rows ? propertyOptionLabel(o) : o}
            </option>
          ))}
        </select>
      );
    }

    if (spec.type === "ed25519") {
      return (
        <Ed25519KeyInput
          showGuide={keyGuide}
          value={raw}
          apiKey={values.api_key}
          readOnly={readOnly}
          onChange={(v) => setField(spec.name, v)}
          onApiKeyChange={
            hideHmac && specs.some((s) => s.name === "api_key")
              ? (v) => setField("api_key", v)
              : undefined
          }
        />
      );
    }

    if (spec.type === "script") {
      return readOnly ? (
        <pre className="max-h-48 overflow-auto rounded-[var(--radius-input)] border border-[var(--border)] bg-[var(--input-bg)] p-3 text-xs">
          {String(raw || "")}
        </pre>
      ) : (
        <ScriptEditor value={String(raw || "")} onChange={(v) => setField(spec.name, v)} />
      );
    }

    if (spec.type === "datetime") {
      return (
        <input
          type="datetime-local"
          step="1"
          value={msToDatetimeLocal(raw)}
          disabled={fieldDisabled}
          className={rows ? (compact ? "w-[7.5rem]" : "min-w-[14rem] max-w-full") : undefined}
          onChange={(e) => setField(spec.name, datetimeLocalToMs(e.target.value))}
        />
      );
    }

    if (spec.name === "qty") {
      const unit = values.qty_unit;
      const amountText = engineQtyAmountText(raw);
      const mode = engineQtyMode(raw ?? spec.default, unit);
      // Contracts need the engine's qty_unit; older servers only know USD and percent.
      const modes = ENGINE_QTY_MODES.filter((m) => m.id !== "contracts" || has("qty_unit"));
      const write = (text: string, next: EngineQtyMode) =>
        has("qty_unit")
          ? setFields({ [spec.name]: formatEngineQty(text, next), qty_unit: engineQtyUnit(next) })
          : setField(spec.name, formatEngineQty(text, next));
      return (
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="decimal"
            aria-label="Default order size"
            value={amountText}
            disabled={fieldDisabled}
            className={rows ? "w-[7.5rem]" : undefined}
            onChange={(e) => {
              const text = e.target.value;
              if (text === "" || text === ".") {
                setField(spec.name, "");
                return;
              }
              if (!/^\d*\.?\d*$/.test(text)) return;
              write(text, mode);
            }}
          />
          <select
            aria-label="Order size unit"
            value={mode}
            disabled={fieldDisabled}
            className={rows ? "min-w-[8.5rem]" : undefined}
            onChange={(e) => write(amountText || "100", e.target.value as EngineQtyMode)}
          >
            {modes.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (rows && isLeverageField(spec)) {
      return (
        <LeverageInput
          label={propertyLabel(spec)}
          margin={raw}
          disabled={fieldDisabled}
          onChange={(margin) => setField(spec.name, margin)}
        />
      );
    }

    if (spec.type === "int" || spec.type === "float") {
      const input = (
        <input
          type="text"
          inputMode={spec.type === "float" ? "decimal" : "numeric"}
          value={raw === undefined || raw === null ? "" : String(raw)}
          disabled={fieldDisabled}
          className={rows ? "w-[7.5rem]" : undefined}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") {
              setField(spec.name, "");
              return;
            }
            if (spec.type === "int") {
              if (!/^-?\d*$/.test(v)) return;
              setField(spec.name, v === "-" ? v : parseInt(v, 10));
              return;
            }
            if (!isPartialDecimalInput(v)) return;
            const normalized = v.replace(",", ".");
            // Keep incomplete decimals while typing (e.g. "0.").
            if (normalized === "-" || /\.$/.test(normalized)) {
              setField(spec.name, normalized);
              return;
            }
            const n = parseFloat(normalized);
            if (Number.isFinite(n)) setField(spec.name, n);
          }}
          onBlur={() => {
            if (raw === undefined || raw === null || raw === "" || typeof raw === "number") return;
            const n = parseDecimalInput(String(raw));
            if (Number.isFinite(n)) setField(spec.name, n);
          }}
        />
      );
      if (rows && spec.name === "fee" && has("fee_unit")) {
        return (
          <div className="flex items-center gap-2">
            {input}
            <select
              aria-label="Commission type"
              value={String(values.fee_unit || "percent")}
              disabled={fieldDisabled}
              className="min-w-[8.5rem]"
              onChange={(e) => setField("fee_unit", e.target.value)}
            >
              {ENGINE_FEE_UNITS.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
        );
      }
      if (!unit) return input;
      return (
        <div className="flex items-center gap-2">
          {input}
          <span className={unitClass}>{unit}</span>
        </div>
      );
    }

    const textInput = (
      <input
        type={secret && !reveal[spec.name] ? "password" : "text"}
        value={
          readOnly && secret
            ? "••••••••"
            : isRedacted(raw)
              ? reveal[spec.name]
                ? ""
                : "••••••••"
              : String(shown)
        }
        placeholder={isRedacted(raw) ? "Leave blank to keep existing" : undefined}
        disabled={fieldDisabled}
        className={rows ? (compact ? "w-[7.5rem]" : "min-w-[14rem] max-w-full") : undefined}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "" && isRedacted(raw)) setField(spec.name, REDACTED_VALUE);
          else setField(spec.name, v);
        }}
        onFocus={() => {
          if (isRedacted(raw) && !fieldDisabled) setField(spec.name, "");
        }}
      />
    );
    if (!unit) return textInput;
    return (
      <div className="flex items-center gap-2">
        {textInput}
        <span className={unitClass}>{unit}</span>
      </div>
    );
  }

  if (loading) {
    return <div className="text-sm text-[var(--text-secondary)]">Loading parameters…</div>;
  }

  if (specs.length === 0) {
    return <div className="text-sm text-[var(--text-muted)]">No parameters for this provider.</div>;
  }

  const fieldGrid = columnsClass(columns);
  const rowsGrid =
    rows && columns >= 3
      ? "grid gap-x-6 sm:grid-cols-2 xl:grid-cols-3"
      : rows && columns === 2
        ? "grid gap-x-8 sm:grid-cols-2"
        : null;

  return (
    <div
      className={
        rows
          ? rowsGrid
            ? "flex w-full flex-col gap-5"
            : "mx-auto flex w-full max-w-2xl flex-col gap-5"
          : "flex flex-col gap-3"
      }
    >
      {showFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Filter ${specs.length} parameters…`}
            className="max-w-sm"
            aria-label="Filter parameters"
          />
          {needle && (
            <span className="text-xs text-[var(--text-muted)]">
              {filteredGroups.reduce((n, [, f]) => n + f.length, 0)} match
              {filteredGroups.reduce((n, [, f]) => n + f.length, 0) === 1 ? "" : "es"}
            </span>
          )}
        </div>
      )}

      {filteredGroups.length === 0 ? (
        <div className="text-sm text-[var(--text-muted)]">No parameters match “{filter.trim()}”.</div>
      ) : (
        filteredGroups.map(([group, fields]) => {
          const isCollapsed = !rows && showGroupHeaders && !needle && collapsed[group];
          return (
            <section
              key={group}
              className={
                compact
                  ? "border-b border-[var(--divider)] px-4 py-3.5"
                  : dense && showGroupHeaders
                    ? "rounded-[10px] border border-[var(--border)] bg-[var(--bg-card-elevated)]/40 px-3 py-3"
                    : undefined
              }
            >
              {showGroupHeaders &&
                (rows ? (
                  <div
                    className={
                      compact
                        ? "mb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]"
                        : "mb-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]"
                    }
                  >
                    {group}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="mb-2 flex w-full items-center justify-between gap-2 text-left"
                    onClick={() => toggleGroup(group)}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                      {group}
                      <span className="ml-2 font-normal normal-case tracking-normal opacity-70">
                        ({fields.length})
                      </span>
                    </span>
                    <span className="text-xs text-[var(--text-muted)]">{isCollapsed ? "Show" : "Hide"}</span>
                  </button>
                ))}

              {!isCollapsed &&
                (rows ? (
                  <div
                    className={
                      compact
                        ? "flex flex-col"
                        : rowsGrid
                          ? `${rowsGrid} border-y border-[var(--divider)]`
                          : "divide-y divide-[var(--divider)] border-y border-[var(--divider)]"
                    }
                  >
                    {fields.map((spec) => {
                      const desc = usefulDescription(spec);
                      const secret = spec.secret && maskSecrets;
                      const wide = isWideField(spec);
                      const cellClass = compact
                        ? "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 py-1.5"
                        : rowsGrid
                          ? wide
                            ? "col-span-full flex flex-col gap-2 border-b border-[var(--divider)] py-3"
                            : "flex items-center justify-between gap-3 border-b border-[var(--divider)] py-3"
                          : wide
                            ? "flex flex-col gap-2 py-3"
                            : "flex items-center justify-between gap-6 py-3";

                      if (spec.type === "bool") {
                        return (
                          <label
                            key={spec.name}
                            className={
                              compact
                                ? "grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 py-1.5"
                                : rowsGrid
                                  ? "flex cursor-pointer items-center justify-between gap-3 border-b border-[var(--divider)] py-3"
                                  : "flex cursor-pointer items-center justify-between gap-6 py-3"
                            }
                          >
                            <span className="min-w-0">
                              <span className={`flex items-center gap-1.5 ${compact ? "text-[13px]" : "text-sm"} text-[var(--text-primary)]`}>
                                <FieldLabelName spec={spec} />
                                {desc && !compact && <TipIcon tip={desc} />}
                              </span>
                              {compact && desc && (
                                <span className="mt-0.5 block text-[11px] leading-snug text-[var(--text-muted)]">{desc}</span>
                              )}
                            </span>
                            {renderControl(spec)}
                          </label>
                        );
                      }

                      return (
                        <div key={spec.name} className={cellClass}>
                          <div className="min-w-0">
                            <div className={`flex items-center gap-1.5 ${compact ? "text-[13px]" : "text-sm"} text-[var(--text-primary)]`}>
                              <FieldLabelName spec={spec} />
                              {spec.secret && spec.type !== "ed25519" && (
                                <span className="text-xs text-[var(--warning)]">secret</span>
                              )}
                              {desc && !compact && <TipIcon tip={desc} />}
                              {secret && spec.type !== "ed25519" && !readOnly && (
                                <button
                                  type="button"
                                  className="shrink-0 text-xs text-[var(--primary)]"
                                  onClick={() => setReveal((r) => ({ ...r, [spec.name]: !r[spec.name] }))}
                                >
                                  {reveal[spec.name] ? "Hide" : "Show"}
                                </button>
                              )}
                            </div>
                            {compact && desc && (
                              <div className="mt-0.5 text-[11px] leading-snug text-[var(--text-muted)]">{desc}</div>
                            )}
                          </div>
                          <div
                            className={
                              wide
                                ? "w-full"
                                : `flex shrink-0 justify-end${spec.disabled ? " opacity-60" : ""}`
                            }
                          >
                            {renderControl(spec)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={fieldGrid}>
                    {fields.map((spec) => {
                      const raw = values[spec.name];
                      const secret = spec.secret && maskSecrets;
                      const wide = columns > 1 && isWideField(spec);
                      const desc = usefulDescription(spec);
                      const tip = desc || undefined;
                      const label = propertyLabel(spec);
                      const fieldDisabled = readOnly || spec.disabled === true;

                      if (spec.type === "bool") {
                        return (
                          <label
                            key={spec.name}
                            className={
                              wide
                                ? "col-span-full flex cursor-pointer items-center gap-2 py-0.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                : "flex cursor-pointer items-center gap-2 py-0.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            }
                            title={tip}
                          >
                            <input
                              type="checkbox"
                              checked={raw === true || raw === "true"}
                              disabled={fieldDisabled}
                              onChange={(e) => setField(spec.name, e.target.checked)}
                            />
                            <span className="min-w-0 truncate leading-none">{label}</span>
                          </label>
                        );
                      }

                      return (
                        <label
                          key={spec.name}
                          className={
                            wide
                              ? "form-row !mb-0 !gap-1.5 col-span-full"
                              : dense
                                ? "form-row !mb-0 !gap-1.5"
                                : "form-row"
                          }
                          title={tip}
                        >
                          {spec.type !== "ed25519" && (
                          <span className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                            <span className="truncate">
                              {label}
                              {spec.secret && (
                                <span className="ml-2 normal-case tracking-normal text-[var(--warning)]">secret</span>
                              )}
                            </span>
                            {secret && !readOnly && (
                              <button
                                type="button"
                                className="shrink-0 normal-case tracking-normal text-xs text-[var(--primary)]"
                                onClick={() => setReveal((r) => ({ ...r, [spec.name]: !r[spec.name] }))}
                              >
                                {reveal[spec.name] ? "Hide" : "Show"}
                              </button>
                            )}
                          </span>
                          )}

                          {renderControl(spec)}

                          {!dense && desc && spec.type !== "ed25519" && (
                            <span className="text-xs text-[var(--text-muted)]">{desc}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                ))}
            </section>
          );
        })
      )}
    </div>
  );
}

export function buildConfigPayload(
  values: Record<string, unknown>,
  specs: ParamSpec[]
): Record<string, unknown> {
  const list = Array.isArray(specs) ? specs : [];
  const secrets = new Set(list.filter((s) => s.secret).map((s) => s.name));
  const ed25519 = new Set(list.filter((s) => s.type === "ed25519").map((s) => s.name));
  const out: Record<string, unknown> = {};
  for (const s of list) {
    const k = s.name;
    const v = values[k];
    if (secrets.has(k)) {
      if (isRedacted(v) || v === "••••••••") {
        out[k] = REDACTED_VALUE;
      } else if (v !== undefined && v !== "") {
        out[k] = v;
      }
      continue;
    }
    if (ed25519.has(k)) {
      if (typeof v === "string" && v && !isRedacted(v)) {
        out[k] = toEd25519Wire(v);
      } else if (isRedacted(v) || v === "••••••••") {
        out[k] = REDACTED_VALUE;
      }
      continue;
    }
    if (v === undefined || v === null) continue;
    if (s.type === "float" || s.type === "int") {
      if (v === "") continue;
      const n = typeof v === "number" ? v : parseDecimalInput(String(v));
      if (!Number.isFinite(n)) continue;
      out[k] = s.type === "int" ? Math.trunc(n) : n;
      continue;
    }
    out[k] = v;
  }
  return out;
}
